import {useEffect,useRef,useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import type {Case} from '@artemis-group/aiw-contracts';
import {Button,Textarea} from '@aiw/ui';
import {api,client} from '../lib/api';
import {JobArchiveLink} from './job-archive-link';

/** Requests a human cancellation review; never treats the request as a stop. */
export function PoDeliveryControl({kase}:{kase:Case}){
 const [editing,setEditing]=useState(false),[reason,setReason]=useState(''),[busy,setBusy]=useState(false),[notice,setNotice]=useState('');
 const attempt=useRef<{reason:string;key:string}|null>(null),qc=useQueryClient();
 useEffect(()=>{attempt.current=null;},[kase.delivery_id,kase.delivery_error]);
 const uncertain=kase.delivery_status==='uncertain';
 if(!kase.delivery_id||!['queued','claimed','prepared','drafted','uncertain'].includes(kase.delivery_status??''))return null;
 async function check(){
  if(busy)return;setBusy(true);setNotice('');
  if(attempt.current?.reason!=='check')attempt.current={reason:'check',key:crypto.randomUUID()};
  try{
   const r=await api.propose(client,{command:'check_po_delivery',idempotency_key:attempt.current!.key,args:{delivery_id:kase.delivery_id}});
   setNotice(r.status==='executed'?'Another bounded status check was requested. The order will not be resent.':r.status==='pending'?'The status-check request awaits approval. The order will not be resent.':`Check not started: ${r.reason}`);
   await qc.invalidateQueries();
  }catch{setNotice('The check request could not be verified. Refresh before trying again.');}finally{setBusy(false);}
 }
 async function request(){
  if(busy||reason.trim().length<3)return;
  setBusy(true);setNotice('');
  if(attempt.current?.reason!==reason.trim())attempt.current={reason:reason.trim(),key:crypto.randomUUID()};
  try{
   const r=await api.propose(client,{command:'cancel_po_delivery',idempotency_key:attempt.current!.key,args:{delivery_id:kase.delivery_id,reason:reason.trim()}});
   setNotice(r.status==='pending'?'Cancellation needs your approval in the new review card. Delivery has not been stopped yet.':r.status==='executed'?'Delivery cancelled. The order is a draft again.':`Cancellation was not recorded: ${r.reason}`);
   if(r.status==='pending'||r.status==='executed')setEditing(false);
   await qc.invalidateQueries();
  }catch{setNotice('The cancellation outcome could not be verified. Refresh and check the order before trying again.');}
  finally{setBusy(false);}
 }
 return <div className="rounded-md border bg-card p-3 text-sm">
  {uncertain?<Button size="sm" variant="outline" disabled={busy} onClick={()=>void check()}>Check sending status</Button>:editing?<><label htmlFor={`stop-${kase.id}`}>Why should this delivery stop?</label><Textarea id={`stop-${kase.id}`} value={reason} onChange={e=>setReason(e.target.value)} maxLength={500}/><div className="mt-2 flex gap-2"><Button size="sm" disabled={busy||reason.trim().length<3} onClick={()=>void request()}>Request cancellation review</Button><Button size="sm" variant="ghost" disabled={busy} onClick={()=>setEditing(false)}>Back</Button></div></>:<Button size="sm" variant="outline" disabled={busy} onClick={()=>setEditing(true)}>Stop delivery…</Button>}
  {notice&&<p role="status" className="mt-2">{notice}</p>}
  {/* The archive door, pre-scoped to this job. Its label comes from the
      `document_archive` config, and it stays silent until the archive answers,
      so a job with nothing filed shows no dead link. */}
  <div className="mt-2 border-t pt-2"><JobArchiveLink jobRef={kase.job_ref}/></div>
 </div>;
}
